import React from 'react';
import { ArrowRight, Bike, ShoppingBag, PartyPopper, Tag, MapPin, Moon, Utensils, HandCoins, Handshake, Gift, Play } from 'lucide-react';
import { ABOUT_FEATURES, ABOUT_IMAGE, WHY_CHOOSE_US, WHY_IMAGE, GALLERY_IMAGES, ORDER_URL, YT_VIDEO_ID } from '../mock';
import PageHeader from '../components/PageHeader';
import useSiteSettings from '../hooks/useSiteSettings';
import { useState } from 'react';

const ICONS = { Bike, ShoppingBag, PartyPopper, Tag, MapPin, Moon, Utensils, HandCoins, Handshake, Gift };

export default function About() {
  const [videoOpen, setVideoOpen] = useState(false);
  const settings = useSiteSettings();
  return (
    <main>
      <PageHeader title="About Us" breadcrumb="About" heroImage={settings.aboutHero} />

      {/* Feature grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ABOUT_FEATURES.map((f) => {
            const Icon = ICONS[f.icon];
            return (
              <div key={f.title} className="bg-[#FDF3EC] rounded-3xl p-8 hover:shadow-xl transition-shadow group">
                <div className="w-14 h-14 rounded-2xl bg-[#ED2E2E] text-white flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                  <Icon size={24} />
                </div>
                <div className="eyebrow text-xs mb-2 uppercase tracking-widest">{f.tag}</div>
                <h3 className="font-heading font-extrabold text-xl text-[#0F1C2E] mb-3">{f.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* About Story */}
      <section className="py-20 bg-[#FDF3EC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-12 items-center">
          <img src={ABOUT_IMAGE} alt="story" className="rounded-3xl w-full object-cover shadow-xl" />
          <div>
            <div className="eyebrow mb-3">About Wrappus Donair</div>
            <h2 className="section-title text-3xl md:text-5xl mb-6">We Create The Best Donair in Port Coquitlam</h2>
            <p className="text-gray-700 mb-4">At Wrappus Donair, we’re not just wrapping donairs — we’re wrapping memories. Born in the heart of Coquitlam at 3353 Coast Meridian Rd, our shop was built on one simple idea:</p>
            <p className="italic text-[#ED2E2E] font-semibold text-lg mb-4">Great food doesn’t have to be complicated or expensive.</p>
            <p className="text-gray-700 mb-6">From your post-work cravings to your midnight munchies, we’re here to fuel your day — one perfectly grilled donair at a time.</p>
            <h3 className="font-heading font-bold text-2xl text-[#0F1C2E] mb-3">Our Mission</h3>
            <p className="text-gray-700">We serve honest, hearty, and affordable meals that hit the spot every single time. Whether it’s chicken, beef, lamb, or falafel — we put flavor and freshness first. No fluff. No shortcuts. Just food that speaks for itself.</p>
          </div>
        </div>
      </section>

      {/* Gallery marquee */}
      <section className="py-8 bg-white overflow-hidden">
        <div className="flex gallery-scroll gap-4" style={{ width: 'max-content' }}>
          {[...GALLERY_IMAGES, ...GALLERY_IMAGES, ...GALLERY_IMAGES].map((img, i) => (
            <img key={i} src={img} alt="food" className="w-64 h-64 md:w-80 md:h-80 object-cover rounded-3xl" />
          ))}
        </div>
      </section>

      {/* Why choose us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-12 items-center">
          <img src={WHY_IMAGE} alt="Why choose" className="rounded-3xl w-full object-cover shadow-lg" />
          <div>
            <div className="eyebrow mb-3">Why Choose Us?</div>
            <h2 className="section-title text-3xl md:text-5xl mb-4">Best Quality Food, Honest Pricing & Real Flavors</h2>
            <div className="space-y-5 mt-6">
              {WHY_CHOOSE_US.map((w) => {
                const Icon = ICONS[w.icon];
                return (
                  <div key={w.title} className="flex gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-[#ED2E2E] text-white flex items-center justify-center shrink-0 shadow-md"><Icon size={22} /></div>
                    <div>
                      <h4 className="text-[#F5A623] font-bold text-lg mb-1">{w.title}</h4>
                      <p className="text-gray-600 text-sm">{w.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Video CTA */}
      <section className="relative py-24 bg-[#0F1C2E] overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 flex flex-col items-center text-center">
          <button onClick={() => setVideoOpen(true)} className="w-20 h-20 rounded-full bg-[#ED2E2E] hover:bg-[#C81E1E] text-white flex items-center justify-center shadow-2xl transition-colors mb-6">
            <Play size={28} fill="white" className="ml-1" />
          </button>
          <div className="eyebrow mb-3">Healthy, Tasty & Always Fresh.</div>
          <h2 className="section-title text-white text-3xl md:text-5xl max-w-3xl mb-6">We’re Growing, One Bite at a Time</h2>
          <p className="text-white/70 max-w-2xl mb-4">Wrappus Donair is more than a restaurant — it’s a brand in the making. As we prepare for future franchise locations, we’re staying grounded in what matters: consistency, customer care, and community love.</p>
          <p className="text-white/70 max-w-2xl mb-6">We’ve built something special here. And we’re excited for you to be part of the ride.</p>
          <a href={ORDER_URL} target="_blank" rel="noopener noreferrer" className="btn-wrappus text-sm">Order Online <ArrowRight size={14} /></a>
        </div>
      </section>

      {videoOpen && (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4" onClick={() => setVideoOpen(false)}>
          <div className="relative w-full max-w-4xl aspect-video" onClick={e => e.stopPropagation()}>
            <button onClick={() => setVideoOpen(false)} className="absolute -top-12 right-0 text-white hover:text-[#ED2E2E]">Close ×</button>
            <iframe src={`https://www.youtube.com/embed/${YT_VIDEO_ID}?autoplay=1`} title="Video" allow="autoplay; encrypted-media" allowFullScreen className="w-full h-full rounded-2xl" />
          </div>
        </div>
      )}
    </main>
  );
}
